/**
 * Sprint 24.5 — EquipOnHiresModal.
 *
 * Opened from the install wizard's success step ("Equip on hires →"). A
 * multi-select agent picker that equips the freshly-registered connection on the
 * chosen hires (reuses Sprint 24's equip_agent_with_connection). Agents already
 * equipped are shown as such and excluded from the selection.
 */

import { useMemo, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { NodeAvatar, showToast } from "../primitives";
import {
  type ConnectionKind,
  useAgentsEquippedWith,
  useEquipAgent,
} from "../../../hooks/use-connections";

interface AgentNode {
  id: string;
  name: string;
}

export interface EquipOnHiresModalProps {
  open: boolean;
  connectionKind: ConnectionKind;
  connectionId: string;
  connectionName?: string;
  onClose: () => void;
}

export function EquipOnHiresModal({
  open,
  connectionKind,
  connectionId,
  connectionName,
  onClose,
}: EquipOnHiresModalProps) {
  const equip = useEquipAgent();
  const { data: equippedAgents } = useAgentsEquippedWith(connectionKind, connectionId);
  const agentsQuery = useQuery({
    queryKey: ["agent-nodes-for-equip"],
    queryFn: async (): Promise<AgentNode[]> => {
      const nodes = await mcpClient.listTree({ type: "agent" });
      return nodes
        .filter((n) => n.type === "agent")
        .map((n) => ({ id: n.id, name: n.name }));
    },
    enabled: open,
    staleTime: 20_000,
  });
  // The workspace (root) agent already has every connection workspace-wide
  // (Sprint 24.6), so equipping it is redundant — we label + lock its row.
  const wsInfo = useQuery({
    queryKey: ["workspace-info"],
    queryFn: () => mcpClient.getWorkspaceInfo(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const rootNodeId = wsInfo.data?.root_node_id ?? null;

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const equippedIds = useMemo(
    () => new Set((equippedAgents ?? []).map((a) => a.node_id)),
    [equippedAgents],
  );

  if (!open) return null;

  const agents = agentsQuery.data ?? [];

  function toggle(nodeId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }

  async function confirm() {
    const ids = [...selected];
    if (ids.length === 0) {
      onClose();
      return;
    }
    try {
      for (const nodeId of ids) {
        await equip.mutateAsync({
          node_id: nodeId,
          connection_kind: connectionKind,
          connection_id: connectionId,
        });
      }
      showToast(
        `Equipped ${ids.length} hire${ids.length === 1 ? "" : "s"} with ${connectionName ?? "this connection"}.`,
        "success",
      );
      onClose();
    } catch {
      showToast("Couldn't equip one or more hires — try again.", "error");
    }
  }

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={headerRow}>
          <div>
            <h2 style={h2}>Equip hires</h2>
            <p style={tagline}>
              Choose which hires can use {connectionName ?? "this connection"}'s tools. The
              workspace agent already has every connection — pick individual hires below.
            </p>
          </div>
          <button type="button" style={closeBtn} onClick={onClose} aria-label="Close">
            <i className="ti ti-x" />
          </button>
        </div>

        <div style={list}>
          {agentsQuery.isLoading ? (
            <div style={muted}>Loading hires…</div>
          ) : agents.length === 0 ? (
            <div style={muted}>No hires yet. Hire an agent first, then equip it.</div>
          ) : (
            agents.map((a) => {
              const isRoot = !!rootNodeId && a.id === rootNodeId;
              const already = isRoot || equippedIds.has(a.id);
              const checked = already || selected.has(a.id);
              return (
                <label key={a.id} style={{ ...row, opacity: already ? 0.6 : 1 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={already}
                    onChange={() => toggle(a.id)}
                    style={checkbox}
                  />
                  <NodeAvatar type="agent" name={a.name} size="sm" />
                  <span style={name}>
                    {a.name}
                    {isRoot ? " · Workspace" : ""}
                  </span>
                  {isRoot ? (
                    <span style={equippedTag}>All connections</span>
                  ) : already ? (
                    <span style={equippedTag}>Equipped</span>
                  ) : null}
                </label>
              );
            })
          )}
        </div>

        <div style={footer}>
          <button type="button" style={cancelBtn} onClick={onClose}>
            Done
          </button>
          <button
            type="button"
            style={primaryBtn}
            disabled={selected.size === 0 || equip.isPending}
            onClick={confirm}
          >
            {equip.isPending
              ? "Equipping…"
              : `Equip ${selected.size || ""} hire${selected.size === 1 ? "" : "s"}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 120,
};

const card: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  width: 420,
  maxWidth: "90vw",
  maxHeight: "80vh",
  padding: "22px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  fontFamily: "var(--font-sans)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
};

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
};

const h2: CSSProperties = { fontSize: 18, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const tagline: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)", margin: "4px 0 0" };

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 16,
  padding: 2,
  flexShrink: 0,
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  overflowY: "auto",
  minHeight: 0,
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 8px",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  background: "var(--color-background-secondary)",
};

const checkbox: CSSProperties = { cursor: "pointer", flexShrink: 0 };

const name: CSSProperties = {
  flex: 1,
  fontSize: 13,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const equippedTag: CSSProperties = {
  fontSize: 10,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  flexShrink: 0,
};

const muted: CSSProperties = { fontSize: 12, color: "var(--color-text-tertiary)", padding: "12px 0" };

const footer: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8 };

const cancelBtn: CSSProperties = {
  padding: "6px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "6px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
